import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BrandContext = createContext();

// Neutral defaults \u2014 used only if /api/config fails to load. They must NOT carry
// any one tenant's identity (a failed config on Avia must never fall back to
// "Yamam"). The real per-tenant brand always comes from /api/config.
const DEFAULTS = {
  brandName: '',
  brandNameAr: '',
  brandLogo: '',
  brandDomain: '',
  adminEmail: '',
  brandDescEn: '',
  brandDescAr: '',
  projects: [],
  projectLabels: {},
  salesTeam: [],
  clarityEnabled: false,
  defaultLang: 'en',
  defaultTheme: 'dark',
  brandColor: '',
  refreshBrand: () => Promise.resolve(),
};

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const refreshBrand = React.useCallback(() => {
    return axios.get('/api/config')
      .then(res => setBrand(prev => ({ ...prev, ...res.data })))
      .catch(() => { /* keep current */ });
  }, []);

  useEffect(() => {
    axios.get('/api/config')
      .then(res => setBrand(prev => ({ ...prev, ...res.data })))
      .catch(() => { /* keep defaults */ })
      .finally(() => setLoaded(true));
  }, []);

  // Apply the tenant's brand accent colour (if configured) as the global --accent token.
  useEffect(() => {
    if (brand.brandColor) document.documentElement.style.setProperty('--accent', brand.brandColor);
    else document.documentElement.style.removeProperty('--accent');
  }, [brand.brandColor]);

  // Hold rendering until the tenant config arrives so no default (Yamam)
  // brand name/logo flashes before the real brand loads.
  if (!loaded) {
    return <div style={{ minHeight: '100vh', background: '#0b0d0f' }} />;
  }

  return (
    <BrandContext.Provider value={{ ...brand, refreshBrand }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandContext) || DEFAULTS;
}
